// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "src/components/shared/alerts/Alert/Alert";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Utils
import { isTerminatedExamReport } from "src/utils/exams/examReportsHelper";

// Constants
import { examCardEnums } from "src/constants/enums";

const IOLLensGraph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.IOL_SIMULATION.sectionName,
      }),
    [exam, examReport]
  );

  const IOLList = useMemo(
    () =>
      examReport?.state?.lenses?.filter((lens) => lens.productType === "IOL") ??
      [],
    [examReport?.state?.lenses]
  );

  const preferredBothIOL = useMemo(
    () =>
      examReport?.state?.lenses?.filter(
        (lens) =>
          lens.productType === "IOL" &&
          lens.preferred.leftEye &&
          lens.preferred.rightEye
      ) ?? [],
    [examReport?.state?.lenses]
  );

  const preferredLeftIOL = useMemo(
    () =>
      examReport?.state?.lenses?.filter(
        (lens) =>
          lens.productType === "IOL" &&
          lens.preferred.leftEye &&
          !lens.preferred.rightEye
      ) ?? [],
    [examReport?.state?.lenses]
  );

  const preferredRightIOL = useMemo(
    () =>
      examReport?.state?.lenses?.filter(
        (lens) =>
          lens.productType === "IOL" &&
          !lens.preferred.leftEye &&
          lens.preferred.rightEye
      ) ?? [],
    [examReport?.state?.lenses]
  );

  return (
    <Grid container gap="20px">
      {isTerminated && (
        <Alert
          variant="error"
          alertContent={t("Terminated Exam")}
          props={{ width: "100%" }}
        />
      )}

      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            {t("Among the following IOL options")}:
          </Typography>
          <Box
            component="ul"
            sx={{
              columns: "auto",
              columnWidth: "200px",
              listStyle: "disc",
              paddingLeft: "20px",
              textAlign: "left",
              fontSize: "14px",
            }}
          >
            {IOLList?.map((lens, index) => (
              <li key={`${lens.lensType}-${index}`}>{t(lens.lensType)}</li>
            ))}
          </Box>
          {preferredBothIOL.length > 0 && (
            <>
              <Typography sx={{ fontSize: "14px" }}>
                {t("For both eyes, the patient preferred")}:
              </Typography>
              <Box
                component="ul"
                sx={{
                  columns: "auto",
                  columnWidth: "200px",
                  listStyle: "disc",
                  paddingLeft: "20px",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                {preferredBothIOL?.map((lens, index) => (
                  <li key={`${lens.lensType}-${index}`}>{t(lens.lensType)}</li>
                ))}
              </Box>
            </>
          )}
          {preferredLeftIOL.length > 0 && (
            <>
              <Typography sx={{ fontSize: "14px" }}>
                {t("For the left eye, the patient preferred")}:
              </Typography>
              <Box
                component="ul"
                sx={{
                  columns: "auto",
                  columnWidth: "200px",
                  listStyle: "disc",
                  paddingLeft: "20px",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                {preferredLeftIOL?.map((lens, index) => (
                  <li key={`${lens.lensType}-${index}`}>{t(lens.lensType)}</li>
                ))}
              </Box>
            </>
          )}
          {preferredRightIOL.length > 0 && (
            <>
              <Typography sx={{ fontSize: "14px" }}>
                {t("For the right eye, the patient preferred")}:
              </Typography>
              <Box
                component="ul"
                sx={{
                  columns: "auto",
                  columnWidth: "200px",
                  listStyle: "disc",
                  paddingLeft: "20px",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                {preferredRightIOL?.map((lens, index) => (
                  <li key={`${lens.lensType}-${index}`}>{t(lens.lensType)}</li>
                ))}
              </Box>
            </>
          )}

          {!preferredBothIOL.length &&
            !preferredLeftIOL.length &&
            !preferredRightIOL.length && (
              <Typography sx={{ fontSize: "14px" }}>
                {t("The patient did not select any preference.")}
              </Typography>
            )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default IOLLensGraph;
